import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Hourglass, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { isClinic } from "../lib/site";
import { navigateWithAttribution } from "../lib/analytics";
import SiteHeader from "../components/SiteHeader";

const REDIRECT_DELAY_MS = 500; // tiny grace period so the WP redirect doesn't feel jarring

// Per-site fallback thank-you URL used only when the backend hasn't returned
// one (defensive — the backend always sets it via general.thank_you_url).
const FALLBACK_THANK_YOU = {
  dental: "https://www.whitesmiledental.ie/book-online-thank-you/",
  clinic: "https://whitesmileclinic.ie/bookly-confirmation-thank-you/",
};

export default function SuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const stripeIntentId = params.get("stripe_intent");
  // PayPal appends ?token=<ORDER_ID>&PayerID=... to our return_url
  const paypalOrderId = params.get("paypal_order") || params.get("token");
  const bookingId = params.get("booking_id") || sessionStorage.getItem("ws_last_booking");
  const isOnlinePayment = !!sessionId || !!paypalOrderId || !!stripeIntentId;
  const [data, setData] = useState(() => (isOnlinePayment ? null : { booking: { payment_status: "not_required" } }));
  const [error, setError] = useState(null);
  // React 18 dev StrictMode double-mounts components. These refs ensure each
  // finalize / capture call fires exactly once across remounts.
  const stripeIntentFiredRef = useRef(false);
  const paypalCaptureFiredRef = useRef(false);

  // Inline Stripe Elements path — the browser already confirmed the card,
  // so just ask the server to verify + finalize.
  useEffect(() => {
    if (!stripeIntentId) return;
    if (stripeIntentFiredRef.current) return;
    stripeIntentFiredRef.current = true;
    let stopped = false;
    (async () => {
      try {
        const res = await api.stripeFinalizeIntent(stripeIntentId);
        if (stopped) return;
        setData(res);
      } catch (e) {
        setError("Couldn't verify your card payment. Please contact us.");
      }
    })();
    return () => { stopped = true; };
  }, [stripeIntentId]);

  // Capture PayPal once when we land on the page with an order id
  useEffect(() => {
    if (!paypalOrderId) return;
    if (paypalCaptureFiredRef.current) return;
    paypalCaptureFiredRef.current = true;
    let stopped = false;
    (async () => {
      try {
        const res = await api.paypalCapture(paypalOrderId);
        if (stopped) return;
        setData(res);
        if (res.payment_status !== "paid") {
          setError("PayPal payment did not complete. Please try again or contact us.");
        }
      } catch (e) {
        setError("Couldn't verify PayPal payment. Please contact us.");
      }
    })();
    return () => { stopped = true; };
  }, [paypalOrderId]);

  // Poll Stripe status when a session_id is present
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      try {
        const res = await api.paymentStatus(sessionId);
        if (stopped) return;
        setData(res);
        if (res.payment_status === "paid") return;
        if (res.status === "expired") {
          setError("Payment session expired. Please try again.");
          return;
        }
        if (attempts++ < 8) setTimeout(poll, 2000);
      } catch (e) {
        if (attempts++ < 8) setTimeout(poll, 2000);
        else setError("Couldn't verify payment. Please contact us.");
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  // Once payment (or free booking) is confirmed, redirect to the WP thank-you
  // page almost immediately — that page fires the Meta/Google conversions.
  // navigateWithAttribution forwards utm_* params and lets gtag's auto-linker
  // append `_gl` so the GA4 session carries over to whitesmileclinic.ie.
  useEffect(() => {
    if (!data || error) return;
    const paid = data?.payment_status === "paid" || data?.booking?.payment_status === "paid" || data?.booking?.payment_status === "not_required";
    if (!paid) return;
    const fallback = FALLBACK_THANK_YOU[isClinic() ? "clinic" : "dental"];
    const thankYou = data.thank_you_url || fallback;
    const t = setTimeout(() => { navigateWithAttribution(thankYou); }, REDIRECT_DELAY_MS);
    return () => clearTimeout(t);
  }, [data, error]);

  // Safety net: if for any reason the data-driven redirect never fires (race
  // conditions, network blip after the server already booked Aerona, etc.),
  // redirect anyway after 6 s. The booking is already created server-side at
  // this point — the patient should not be left staring at a spinner.
  useEffect(() => {
    if (error) return;
    const fallback = setTimeout(() => {
      const perSiteFallback = FALLBACK_THANK_YOU[isClinic() ? "clinic" : "dental"];
      navigateWithAttribution((data && data.thank_you_url) || perSiteFallback);
    }, 6000);
    return () => clearTimeout(fallback);
  }, [error, data]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="ws-card p-8 sm:p-10 text-center" data-testid="success-card">
          {error ? (
            <>
              <h1 className="text-2xl sm:text-3xl font-extrabold mb-3 text-[#D0021B]" style={{ fontFamily: "Manrope" }}>Something went wrong</h1>
              <p className="text-[#5C5C5C] mb-6" data-testid="success-error">{error}</p>
              <Link to="/" className="ws-btn-primary inline-block" data-testid="success-try-again">Try again</Link>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F0FE] grid place-items-center mb-4">
                <Hourglass className="w-8 h-8 text-[#0466ff] animate-pulse" strokeWidth={1.5} />
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold mb-3" style={{ fontFamily: "Manrope" }}>
                Please wait while we confirm your booking…
              </h1>
              <div className="flex items-center justify-center text-[#5C5C5C]" data-testid="success-loading">
                <Loader2 className="w-5 h-5 animate-spin text-[#0466ff]" strokeWidth={2} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
